import {
  Button,
  Card,
  Col,
  DatePicker,
  DeleteButton,
  EditButton,
  Form,
  Icons,
  Input,
  List,
  Row,
  Select,
  ShowButton,
  Space,
  Switch,
  Table,
  Tag,
  TextField,
  useTable,
} from "@pankod/refine-antd";
import {
  HttpError,
  IResourceComponentsProps,
  useUpdate,
} from "@pankod/refine-core";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "@pankod/refine-react-router-v6";

import { IFeatureFlag, IFeatureFlagFilters } from "interfaces/feature-flags";
import { FeatureFlagsHeaderBreadcrumb } from "./components/feature-flags-header-breadcrumb";
import {
  FEATURE_FLAGS_RESOURCE,
  FEATURE_FLAGS_TEXTS,
} from "./utils/feature-flags.constants";
import {
  buildFeatureFlagFilters,
  buildFeatureFlagSearchFormValues,
  FEATURE_FLAGS_PAGINATION_DEFAULTS,
} from "./utils/feature-flags.filters";
import {
  FEATURE_FLAG_CONTEXT_OPTIONS,
  FEATURE_FLAG_STATUS_OPTIONS,
  FEATURE_FLAG_TYPE_OPTIONS,
  FEATURE_FLAG_TYPE_TAG_STYLE_MAP,
  getFeatureFlagExpirationInfo,
  getFeatureFlagContextLabel,
  getFeatureFlagStatusLabel,
  getFeatureFlagTypeLabel,
} from "./utils/feature-flags.helpers";
import {
  buildFeatureFlagListSearch,
  parseFeatureFlagListUrlState,
} from "./utils/feature-flags.url-state";
import { ToggleConfirmationModal } from "./components/toggle-confirmation-modal";

const renderText = (value?: string | null) => (
  <TextField value={value || "-"} />
);

const renderStringList = (values?: string[]) =>
  values?.length ? <TextField value={values.join(", ")} /> : renderText();

const CONTEXT_ICON_MAP: Record<IFeatureFlag["contexts"], React.ReactNode> = {
  frontend: <Icons.DesktopOutlined />,
  backend: <Icons.ApiOutlined />,
  both: <Icons.AppstoreOutlined />,
};

const renderContext = (value?: IFeatureFlag["contexts"]) =>
  value ? (
    <Space direction="vertical" size={4}>
      <Space size={8} style={{ color: "#6A6A6A", fontSize: 14 }}>
        {CONTEXT_ICON_MAP[value]}
        <span>{getFeatureFlagContextLabel(value)}</span>
      </Space>
    </Space>
  ) : (
    renderText()
  );

const renderExpiration = (value?: string | null) => {
  if (!value) {
    return renderText();
  }

  const expirationInfo = getFeatureFlagExpirationInfo(value);

  return (
    <Space direction="vertical" size={0} style={{ alignItems: "flex-start" }}>
      <TextField value={dayjs(value).format("DD/MM/YYYY")} />
      {expirationInfo && (
        <span
          style={{
            color: expirationInfo.color,
            fontSize: 11,
            lineHeight: 1.2,
            textAlign: "left",
          }}
        >
          {expirationInfo.text}
        </span>
      )}
    </Space>
  );
};

const listTitle = (
  <div>
    <div>{FEATURE_FLAGS_TEXTS.title}</div>
    <div
      style={{
        color: "#888",
        fontSize: 14,
        fontWeight: 400,
      }}
    >
      {FEATURE_FLAGS_TEXTS.subtitle}
    </div>
  </div>
);

export const FeatureFlagsList: React.FC<IResourceComponentsProps> = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [initialLocationSearchState] = useState(() =>
    parseFeatureFlagListUrlState(search),
  );
  const [selectedToggleFlag, setSelectedToggleFlag] =
    useState<IFeatureFlag | null>(null);
  const [submittedSearchKey, setSubmittedSearchKey] = useState(
    initialLocationSearchState.searchKey,
  );
  const [resolvedSearchKey, setResolvedSearchKey] = useState(
    initialLocationSearchState.searchKey,
  );
  const [pendingTableReset, setPendingTableReset] = useState(false);

  const handleSearch = (values: IFeatureFlagFilters) => {
    const filters = buildFeatureFlagFilters(values);
    const nextHasSearchFilters = filters.length > 0;
    const nextSearchKey = nextHasSearchFilters
      ? buildFeatureFlagListSearch(values, {
          current: FEATURE_FLAGS_PAGINATION_DEFAULTS.initialCurrent,
          pageSize: FEATURE_FLAGS_PAGINATION_DEFAULTS.initialPageSize,
        })
      : "";

    setSubmittedSearchKey(nextSearchKey);
    setPendingTableReset(true);

    navigate(
      {
        search: buildFeatureFlagListSearch(values, {
          current: FEATURE_FLAGS_PAGINATION_DEFAULTS.initialCurrent,
          pageSize: FEATURE_FLAGS_PAGINATION_DEFAULTS.initialPageSize,
        }),
      },
      { replace: true },
    );

    if (!nextHasSearchFilters) {
      setResolvedSearchKey("");
    }

    return filters;
  };

  const {
    tableProps,
    searchFormProps,
    tableQueryResult,
    filters,
  } = useTable<IFeatureFlag, HttpError, IFeatureFlagFilters>({
    resource: FEATURE_FLAGS_RESOURCE,
    initialCurrent: initialLocationSearchState.current,
    initialPageSize: initialLocationSearchState.pageSize,
    hasPagination: FEATURE_FLAGS_PAGINATION_DEFAULTS.hasPagination,
    initialFilter: initialLocationSearchState.filters,
    syncWithLocation: false,
    onSearch: handleSearch,
    queryOptions: {
      enabled: true,
    },
  });

  const { mutate: toggleFeatureFlag, isLoading: isTogglingFeatureFlag } =
    useUpdate<IFeatureFlag, HttpError, {}>();

  const handleCleanFilters = () => {
    searchFormProps.form?.resetFields();
    setSubmittedSearchKey("");
    setResolvedSearchKey("");
    setPendingTableReset(true);
    navigate({ search: "" }, { replace: true });
    searchFormProps.onFinish?.({} as IFeatureFlagFilters);
  };

  const handleToggleRequest = (record: IFeatureFlag) => {
    setSelectedToggleFlag(record);
  };

  const handleToggleCancel = () => {
    setSelectedToggleFlag(null);
  };

  const handleToggleConfirm = () => {
    if (!selectedToggleFlag) {
      return;
    }

    toggleFeatureFlag(
      {
        resource: FEATURE_FLAGS_RESOURCE,
        id: selectedToggleFlag.key,
        values: {},
        metaData: { URLSuffix: "toggle", method: "PATCH" },
        successNotification: {
          type: "success",
          message: FEATURE_FLAGS_TEXTS.successTitle,
          description: FEATURE_FLAGS_TEXTS.successMessage,
        },
      },
      {
        onSuccess: () => {
          setSelectedToggleFlag(null);
          tableQueryResult.refetch();
        },
        onError: () => {
          setSelectedToggleFlag(null);
        },
      },
    );
  };

  const isTableFetching = Boolean(tableQueryResult.isFetching);
  const isTableLoading = tableQueryResult.isLoading || isTableFetching;
  const shouldHidePreviousResults = isTableLoading && pendingTableReset;

  useEffect(() => {
    searchFormProps.form?.resetFields();

    if (!initialLocationSearchState.hasSearchFilters) {
      setResolvedSearchKey("");
      return;
    }

    searchFormProps.form?.setFieldsValue(initialLocationSearchState.values);

    const compactSearch = buildFeatureFlagListSearch(
      initialLocationSearchState.values,
      {
        current: initialLocationSearchState.current,
        pageSize: initialLocationSearchState.pageSize,
      },
    );

    if (search !== compactSearch) {
      navigate({ search: compactSearch }, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (isTableLoading) {
      return;
    }

    if (submittedSearchKey !== resolvedSearchKey) {
      setResolvedSearchKey(submittedSearchKey);
    }
    setPendingTableReset(false);
  }, [
    isTableLoading,
    pendingTableReset,
    resolvedSearchKey,
    submittedSearchKey,
  ]);

  const appliedSearchValues = useMemo(
    () => buildFeatureFlagSearchFormValues(filters ?? []),
    [filters],
  );

  const handleTableChange = (...args: Parameters<NonNullable<typeof tableProps.onChange>>) => {
    const [pagination] = args;
    const nextCurrent =
      pagination.current ?? FEATURE_FLAGS_PAGINATION_DEFAULTS.initialCurrent;
    const nextPageSize =
      pagination.pageSize ?? FEATURE_FLAGS_PAGINATION_DEFAULTS.initialPageSize;

    if (buildFeatureFlagFilters(appliedSearchValues as IFeatureFlagFilters).length) {
      setPendingTableReset(true);
      setSubmittedSearchKey(
        JSON.stringify({
          values: appliedSearchValues,
          current: nextCurrent,
          pageSize: nextPageSize,
        }),
      );
      navigate(
        {
          search: buildFeatureFlagListSearch(appliedSearchValues, {
            current: nextCurrent,
            pageSize: nextPageSize,
          }),
        },
        { replace: true },
      );
    }

    tableProps.onChange?.(...args);
  };

  const tableDataSource = shouldHidePreviousResults
    ? []
    : tableProps.dataSource;

  const tablePagination = {
    ...tableProps.pagination,
    showTotal: (total: number, range: [number, number]) =>
      `${range[0]}-${range[1]} de ${total} registros`,
  };

  return (
    <List
      title={listTitle}
      breadcrumb={<FeatureFlagsHeaderBreadcrumb />}
      canCreate
      createButtonProps={{ children: "Incluir", type: "primary" }}
      headerButtonProps={{ style: { marginTop: 14 } }}
      headerButtons={({ defaultButtons }) => defaultButtons}
    >
      <Card title={FEATURE_FLAGS_TEXTS.filterTitle}>
        <Form {...searchFormProps} layout="vertical">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="name" label="Nome">
                <Input placeholder="Digite o nome" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item name="key" label="Key">
                <Input placeholder="Key" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item name="enabled" label="Status">
                <Select
                  allowClear
                  options={FEATURE_FLAG_STATUS_OPTIONS}
                  placeholder="Selecione o status"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item name="flag_type" label="Tipo">
                <Select
                  allowClear
                  options={FEATURE_FLAG_TYPE_OPTIONS}
                  placeholder="Selecione o tipo"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item name="contexts" label="Contexto">
                <Select
                  allowClear
                  options={FEATURE_FLAG_CONTEXT_OPTIONS}
                  placeholder="Selecione o contexto"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item name="target_repos" label="Repositório (consumo)">
                <Input placeholder="Digite o repositório" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item name="tags" label="Tags">
                <Input placeholder="Digite a tag" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item name="expires_at" label="Expiração">
                <DatePicker
                  format="DD/MM/YYYY"
                  placeholder="Selecione a data"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} style={{ textAlign: "right" }}>
              <Space>
                <Button
                  icon={<Icons.ClearOutlined />}
                  onClick={handleCleanFilters}
                >
                  Limpar
                </Button>
                <Button
                  htmlType="submit"
                  icon={<Icons.SearchOutlined />}
                  type="primary"
                >
                  Buscar
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      <Table
        {...tableProps}
        dataSource={tableDataSource}
        rowKey="key"
        loading={isTableLoading}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
        pagination={tablePagination}
      >
        <Table.Column
          dataIndex="name"
          title="Nome"
          render={(value) => renderText(value)}
        />
        <Table.Column
          dataIndex="key"
          title="Key"
          render={(value) => renderText(value)}
        />
        <Table.Column<IFeatureFlag>
          dataIndex="flag_type"
          title="Tipo"
          render={(value: IFeatureFlag["flag_type"]) => (
            <Tag
              style={{
                ...FEATURE_FLAG_TYPE_TAG_STYLE_MAP[value],
                border: "none",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 32,
                minWidth: 72,
                textAlign: "center",
              }}
            >
              {getFeatureFlagTypeLabel(value)}
            </Tag>
          )}
        />
        <Table.Column<IFeatureFlag>
          dataIndex="contexts"
          title="Contexto"
          render={(value) => renderContext(value)}
        />
        <Table.Column<IFeatureFlag>
          dataIndex="target_repos"
          title="Repositório"
          render={(values) => renderStringList(values)}
        />
        <Table.Column<IFeatureFlag>
          dataIndex="enabled"
          title="Status"
          render={(value, record) => (
            <Space>
              <Switch
                checked={Boolean(value)}
                onChange={() => handleToggleRequest(record)}
                style={Boolean(value) ? { backgroundColor: "#3F8E12" } : {}}
              />
              <TextField value={getFeatureFlagStatusLabel(Boolean(value))} />
            </Space>
          )}
        />
        <Table.Column
          dataIndex="expires_at"
          title="Expiração"
          render={(value) => renderExpiration(value)}
        />
        <Table.Column<IFeatureFlag>
          dataIndex="tags"
          title="Tags"
          render={(values) => renderStringList(values)}
        />
        <Table.Column<IFeatureFlag>
          title="Ações"
          render={(_, record) => (
            <Space>
              <ShowButton
                hideText
                size="small"
                recordItemId={record.key}
                resourceNameOrRouteName={FEATURE_FLAGS_RESOURCE}
              />
              <EditButton
                hideText
                size="small"
                recordItemId={record.key}
                resourceNameOrRouteName={FEATURE_FLAGS_RESOURCE}
              />
              <DeleteButton
                hideText
                size="small"
                recordItemId={record.key}
                resourceNameOrRouteName={FEATURE_FLAGS_RESOURCE}
                confirmTitle="Deseja excluir?"
                confirmOkText="Deletar"
                successNotification={{
                  type: "success",
                  message: FEATURE_FLAGS_TEXTS.successTitle,
                  description: FEATURE_FLAGS_TEXTS.successMessage,
                }}
              />
            </Space>
          )}
        />
      </Table>
      <ToggleConfirmationModal
        visible={Boolean(selectedToggleFlag)}
        enabled={selectedToggleFlag?.enabled}
        loading={isTogglingFeatureFlag}
        onCancel={handleToggleCancel}
        onConfirm={handleToggleConfirm}
      />
    </List>
  );
};
